// src/Phaser/engine/WinConditionManager.ts
import type { WinCondition } from "../../types/config";
import type { PhaserScene } from "../PhaserScene";
import type { EventManager } from "./EventManager";

/** Interne: on garde le type original + flag _done */
type InternalWinCondition = WinCondition & { _done: boolean };

type WinEvt =
  | { type: "object:picked"; by?: string; charId?: string; objectId: string }
  | { type: "say:phrase"; charId: string; phrase: string }
  // tu peux en ajouter d’autres plus tard:
  | { type: "collision:char-char"; aId?: string; bId?: string }
  | { type: "collision:char-immovable"; charId?: string; objectId?: string }
  | { type: "collision:char-push"; charId?: string; objectId?: string };

export class WinConditionManager {
  private conditions: InternalWinCondition[];
  private scene: PhaserScene;
  private eventManager: EventManager;
  private allConditionsMet = false;

  // unsubscribe functions for registered listeners
  private unsubs: Array<() => void> = [];

  // tolérance par défaut (px) pour les comparaisons de position
  private positionTolerance: number;

  constructor(
    conditions: WinCondition[],
    scene: PhaserScene,
    eventManager: EventManager,
    opts?: { positionTolerance?: number }
  ) {
    this.conditions = conditions.map((c) => ({ ...c, _done: false }));
    this.scene = scene;
    this.eventManager = eventManager;
    this.positionTolerance = Math.max(0, opts?.positionTolerance ?? 5);

    // Listeners — on garde les unsubs pour pouvoir nettoyer
    const offPickup = this.eventManager.on("pickupEvent", (charId: string, objId: string) => {
      this.handlePickup(charId, objId);
    });

    // laissé à titre d’exemple comme dans ton code
    const offSay = this.eventManager.on("sayPhraseEvent", (charId: string, phrase: string) => {
      this.handleSayPhrase(charId, phrase);
    });

    this.unsubs.push(offPickup, offSay);

    const offPickedNew = this.eventManager.on("object:picked", (payload: any) => {
      const by = payload?.by ?? payload?.charId ?? payload?.charID;
      const objectId = payload?.objectId ?? payload?.objId;
      if (by && objectId) this.handlePickup(by, objectId);
    });

    const offSayNew = this.eventManager.on("say:phrase", (payload: any) => {
      if (payload?.charId && typeof payload?.phrase === "string") {
        this.handleSayPhrase(payload.charId, payload.phrase);
      }
    });

    this.unsubs.push(offPickedNew, offSayNew);
  }

  /**
   * Réinitialise les conditions:
   * - remet _done=false
   * - ré‑arme le “win”
   * - (optionnel) vide les conditions si asked
   */
  public reset(options?: { clearConditions?: boolean }) {
    if (options?.clearConditions) {
      this.conditions = [];
    } else {
      this.conditions.forEach((cond) => (cond._done = false));
    }
    this.allConditionsMet = false;
    // on ne touche pas aux listeners ici; si tu veux un wipe total: call dispose()
  }

  /**
   * À appeler régulièrement (scene.update) pour les conditions à évaluer dans le temps.
   */
  public update() {
    let updated = false;

    for (const cond of this.conditions) {
      if (cond._done) continue;

      // 1) Texte exact
      if (
        cond.type === "textContentEquals" &&
        cond.targetTextObjectId &&
        cond.expectedTextContent !== undefined
      ) {
        const txtObj = this.scene.getTextObjectById(cond.targetTextObjectId);
        if (txtObj) {
          const rawCurrent =
            typeof (txtObj as any).getText === "function"
              ? (txtObj as any).getText()
              : (txtObj as any).textString;
          const current = rawCurrent == null ? "" : String(rawCurrent);
          if (current === cond.expectedTextContent) {
            cond._done = true;
            updated = true;
          }
        }
      }

      // 2) Pousser un objet jusqu’à une position
      if (cond.type === "pushObjectToPosition" && cond.targetObjectId && cond.targetPosition) {
        const object = this.scene.getObjectById(cond.targetObjectId);
        if (object) {
          if (this.distance(object.x, object.y, cond.targetPosition.x, cond.targetPosition.y) <= this.positionTolerance) {
            cond._done = true;
            updated = true;
          }
        }
      }

      // 3) Personnage à une position
      if (cond.type === "characterAtPosition" && cond.targetCharacterId && cond.targetPosition) {
        const character = this.scene.getCharacterById(cond.targetCharacterId);
        if (character) {
          if (this.distance(character.x, character.y, cond.targetPosition.x, cond.targetPosition.y) <= this.positionTolerance) {
            cond._done = true;
            updated = true;
          }
        }
      }
    }

    if (updated) this.checkAllConditions();
  }

  /** Débranche tous les listeners et timers (si tu recrées la scène/manager). */
  public dispose() {
    for (const off of this.unsubs) {
      try { off?.(); } catch { /* ignore */ }
    }
    this.unsubs = [];
  }

  // ---------- handlers d’évènements ----------

  private handleSayPhrase(charId: string, phrase: string) {
    let updated = false;
    for (const cond of this.conditions) {
      if (cond._done) continue;
      if (cond.type === "saySpecificPhrase" &&
        cond.targetCharacterId === charId &&
        cond.specificPhrase === phrase) {
        cond._done = true;
        updated = true;
      }
    }
    if (updated) this.checkAllConditions();
  }

  private handlePickup(charId: string, objId: string) {
    let updated = false;
    for (const cond of this.conditions) {
      if (cond._done) continue;
      if (cond.type === "characterPickup") {
        // (1) perso + objet
        if (cond.targetCharacterId && cond.targetObjectId) {
          if (cond.targetCharacterId === charId && cond.targetObjectId === objId) {
            cond._done = true;
            updated = true;
          }
        }
        // (2) seulement objet
        else if (cond.targetObjectId && !cond.targetCharacterId) {
          if (cond.targetObjectId === objId) {
            cond._done = true;
            updated = true;
          }
        }
      }
    }
    if (updated) this.checkAllConditions();
  }

  public evaluateOnEvent(evt: WinEvt) {
    switch (evt.type) {
      case "object:picked": {
        const charId = evt.by ?? evt.charId;
        if (charId && evt.objectId) this.handlePickup(charId, evt.objectId);
        break;
      }
      case "say:phrase": {
        this.handleSayPhrase(evt.charId, evt.phrase);
        break;
      }
      // Ces cases sont “noop” pour l’instant, mais prêtes si tu ajoutes des conditions associées :
      case "collision:char-char":
      case "collision:char-immovable":
      case "collision:char-push": {
        // Si un jour tu ajoutes des win conditions déclenchées par collision,
        // route-les ici (ex: this.handleCollisionX(...))
        break;
      }
    }
  }

  // ---------- utils ----------

  private checkAllConditions() {
    const allMet = this.conditions.length > 0 && this.conditions.every((c) => c._done);
    if (allMet && !this.allConditionsMet) {
      this.allConditionsMet = true;
      this.eventManager.emit("win");
    }
  }

  private distance(ax: number, ay: number, bx: number, by: number) {
    const dx = ax - bx, dy = ay - by;
    return Math.hypot(dx, dy);
  }

  // (optionnel) méthodes pratiques si tu veux modifier à chaud
  public addCondition(cond: WinCondition) {
    this.conditions.push({ ...cond, _done: false });
    // on re-arme le win car nouvelle condition
    this.allConditionsMet = false;
  }

  public removeConditionByIndex(index: number) {
    if (index < 0 || index >= this.conditions.length) return;
    this.conditions.splice(index, 1);
    this.allConditionsMet = false;
  }

  public getConditions(): ReadonlyArray<InternalWinCondition> {
    return this.conditions;
  }
}
